import type {
  AccessRequestStatus,
  KeyEnvironment,
  RequesterType,
} from "@/lib/api/access-requests";
import type { ExpectedDailyVolume } from "@/lib/api/login-user";

export type AdminStatusLabel = "In review" | "Approved" | "Denied";

export function environmentToLabel(
  environment: KeyEnvironment | null | undefined,
): string {
  if (environment === "live") {
    return "Live";
  }
  if (environment === "sandbox") {
    return "Sandbox";
  }
  return "Not provided (legacy request)";
}

/** Compact label for the admin list column (avoids horizontal overflow). */
export function environmentToListLabel(
  environment: KeyEnvironment | null | undefined,
): string {
  if (environment === "live") {
    return "Live";
  }
  if (environment === "sandbox") {
    return "Sandbox";
  }
  return "Legacy";
}

export function statusToLabel(status: AccessRequestStatus): AdminStatusLabel {
  switch (status) {
    case "approved":
      return "Approved";
    case "rejected":
      return "Denied";
    case "pending":
    default:
      return "In review";
  }
}

export function labelToStatus(
  label: AdminStatusLabel | null,
): AccessRequestStatus | undefined {
  switch (label) {
    case "Approved":
      return "approved";
    case "Denied":
      return "rejected";
    case "In review":
      return "pending";
    default:
      return undefined;
  }
}

export function volumeToLabel(
  volume: ExpectedDailyVolume | null | undefined,
): string {
  if (volume == null) {
    return "Not provided";
  }
  switch (volume) {
    case "under_1k":
      return "Under 1k";
    case "1k_10k":
      return "1k – 10k";
    case "10k_100k":
      return "10k – 100k";
    case "100k_plus":
      return "100k+";
    default:
      return volume;
  }
}

export function requesterTypeLabel(type: RequesterType): "Customer" | "Partner" {
  return type === "partner" ? "Partner" : "Customer";
}

export function integrationLabel(integration: string): string {
  if (integration === "zapier") {
    return "Zapier";
  }
  if (integration === "api") {
    return "API – Default";
  }
  return integration;
}

export function formatSubmittedDate(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return iso;
  }
  return date.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

export function formatReviewedMeta(
  reviewedAt: string | null | undefined,
  reviewedByName: string | null | undefined,
): string | null {
  if (!reviewedAt) {
    return null;
  }
  const when = formatSubmittedDate(reviewedAt);
  const name = reviewedByName?.trim();
  if (!name) {
    return when;
  }
  return `${when} · ${name}`;
}

/**
 * Audit line for the admin internal note.
 * Prefers "Updated by …" when update audit differs from create; otherwise "Created by …".
 */
export function formatInternalNoteAudit(input: {
  createdByName?: string | null;
  createdAt?: string | null;
  updatedByName?: string | null;
  updatedAt?: string | null;
}): string | null {
  const updatedAt = input.updatedAt?.trim();
  const createdAt = input.createdAt?.trim();
  const updatedBy = input.updatedByName?.trim();
  const createdBy = input.createdByName?.trim();

  const updatedDiffers =
    Boolean(updatedAt) &&
    Boolean(updatedBy) &&
    (updatedAt !== createdAt || updatedBy !== createdBy);

  if (updatedDiffers && updatedBy && updatedAt) {
    return `Updated by ${updatedBy} on ${formatNoteAuditDate(updatedAt)}`;
  }
  if (createdBy && createdAt) {
    return `Created by ${createdBy} on ${formatNoteAuditDate(createdAt)}`;
  }
  return null;
}

function formatNoteAuditDate(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return iso;
  }
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

/** Compact preview of intended use for the admin list row. */
export function previewIntendedUseCase(
  text: string,
  maxLength = 96,
): string {
  const normalized = text.trim().replace(/\s+/g, " ");
  if (normalized.length <= maxLength) {
    return normalized;
  }
  return `${normalized.slice(0, maxLength - 1)}…`;
}

export function adminInitials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) {
    return "?";
  }
  if (parts.length === 1) {
    return parts[0].slice(0, 1).toUpperCase();
  }
  return `${parts[0].slice(0, 1)}${parts[parts.length - 1].slice(0, 1)}`.toUpperCase();
}
